import DynamicFormRenderer from '@aims/shared/report-schedules/DynamicFormRenderer';
import {
  ScheduleFrequencies,
  allReportScheduleTemplatesForUserQuery,
} from '@aims/shared/report-schedules/constants';
import { scheduledReportForUserQuery } from '@aims/shared/report-schedules/scheduled-report-constants';
import { useQuery } from '@apollo/client';
import { Card, Descriptions, Skeleton, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import CSPage from '../../../components/CSPage';
import CSPageHeader from '../../../components/CSPageHeader';

const { Text } = Typography;

function ViewScheduledReportPage() {
  const { projectId, reportId } = useParams();

  const { data: reportData, loading: loadingReport } = useQuery(
    scheduledReportForUserQuery,
    {
      variables: { _id: reportId },
      skip: !reportId,
    },
  );
  const report = useMemo(
    () => reportData?.scheduledReportForUser || null,
    [reportData],
  );

  const templateId = report?.templateId;
  const { data: templatesData, loading: loadingTemplates } = useQuery(
    allReportScheduleTemplatesForUserQuery,
    {
      variables: { first: 200 },
      fetchPolicy: 'cache-first',
    },
  );
  const template = useMemo(() => {
    if (!templatesData?.allReportScheduleTemplatesForUser?.edges || !templateId)
      return null;
    return templatesData.allReportScheduleTemplatesForUser.edges.find(
      (e) => e.node._id === templateId,
    )?.node;
  }, [templatesData, templateId]);

  const formData = useMemo(() => {
    if (!report?.formData) return {};
    if (typeof report.formData === 'string') {
      try {
        return JSON.parse(report.formData);
      } catch {
        return {};
      }
    }
    return report.formData;
  }, [report]);

  const loading = loadingReport || loadingTemplates;

  if (loading) {
    return (
      <CSPage title="AIMS.org | View Report">
        <CSPageHeader
          title="View Report"
          parents={[
            { label: 'Holistic Projects', link: '/holistic-projects' },
            {
              label: 'Project',
              link: `/holistic-projects/${projectId}/reports`,
            },
          ]}
        />
        <Skeleton active />
      </CSPage>
    );
  }

  return (
    <CSPage title="AIMS.org | View Report">
      <CSPageHeader
        title={`Report - ${report?.frequency || ''}`}
        parents={[
          { label: 'Holistic Projects', link: '/holistic-projects' },
          {
            label: 'Project',
            link: `/holistic-projects/${projectId}/reports`,
          },
        ]}
      />

      <Card style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Frequency">
            <Tag color={ScheduleFrequencies[report?.frequency]?.color}>
              {ScheduleFrequencies[report?.frequency]?.label ||
                report?.frequency}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Due Date">
            {report?.dueDate
              ? dayjs(report.dueDate).format('MMM D, YYYY')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {report?.isApproved ? (
              <Tag color="green">Approved</Tag>
            ) : report?.isSubmitted ? (
              <Tag color="blue">Submitted</Tag>
            ) : (
              <Tag>Draft</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Project #">
            {report?.holisticProject?.projectNumber ||
              report?.holisticProject?.projectNo ||
              '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Project">
            {report?.holisticProject?.projectName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Region">
            {report?.holisticProject?.region ||
              report?.holisticProject?.regionCode ||
              report?.regionCode ||
              '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Submitted">
            {report?.submittedAt
              ? dayjs(report.submittedAt).format('MMM D, YYYY h:mm A')
              : 'Not submitted'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {template?.formFields?.length > 0 && (
        <Card title="Report Details" style={{ marginBottom: 24 }}>
          <DynamicFormRenderer
            formFields={template.formFields}
            formData={formData}
            readOnly
          />
        </Card>
      )}

      {report?.notes && (
        <Card title="Notes" style={{ marginBottom: 24 }}>
          <Text>{report.notes}</Text>
        </Card>
      )}

      {report?.adminNotes && (
        <Card title="Admin Notes" style={{ marginBottom: 24 }}>
          <Text>{report.adminNotes}</Text>
        </Card>
      )}
    </CSPage>
  );
}

export default ViewScheduledReportPage;
